import { UseMutateAsyncFunction } from '@tanstack/react-query'

import { ErrorType } from '@/features/auth/axios-config'
import {
  ErrorResponse,
  RequestCreateInvitation,
  SuccessResponseResponseInvitation,
} from '@/schema'
import { inviteUsersRedirectUrl } from '@/utils'

import {
  CompanyDropdown,
  IAddUsersFormData,
  IInviteUser,
  ResponseProjectDropdown,
} from '../types'

export * from './roleRestrictionUtils'
export * from './stepperUtils'
export * from './validationUtils'

/** mutateAsync returns a promise per call so parallel calls all settle independently (unlike mutate callbacks). */
export type CreateInvitationMutateAsync = UseMutateAsyncFunction<
  SuccessResponseResponseInvitation,
  ErrorType<ErrorResponse>,
  { data: RequestCreateInvitation },
  unknown
>

const buildInvitationPayload = (
  user: IInviteUser,
  formData: IAddUsersFormData
): RequestCreateInvitation => ({
  email: user.email,
  first_name: user.firstName,
  last_name: user.lastName,
  role_id: formData.roleId,
  note: formData.message,
  redirect_url: inviteUsersRedirectUrl(),
  scopes: formData.assignedProjects?.map((project) => ({
    resource_id: project,
    resource_type: 'project',
  })),
})

/**
 * Run all invitation requests in parallel using mutateAsync so each call has its own promise.
 * Always resolves (never rejects) when every request has settled.
 * Use the returned errorsByIndex to show one error toast and display errors per form row.
 * @param formData - The form data containing the invitation details
 * @param mutateAsync - The async mutation (one promise per call; use instead of mutate for parallel calls)
 * @returns An object containing the errors by index
 */
export const runAllInvitationPromises = async (
  formData: IAddUsersFormData,
  mutateAsync: CreateInvitationMutateAsync
): Promise<void> => {
  const promises = formData.users.map((user) =>
    mutateAsync({ data: buildInvitationPayload(user, formData) })
  )

  await Promise.allSettled(promises)
}

/**
 * Converts a flat list of projects (with company context) into companies grouped by company,
 * each with their projects.
 *
 * @param flatProjects - Flat array of ResponseProjectDropdown (one row per project)
 * @returns CompanyDropdown[] - Companies with nested projects, grouped by company_id
 *
 * @example
 * const flat = [
 *   { company_id: 'c1', company_name: 'Acme', project_id: 'p1', project_name: 'Project A' },
 *   { company_id: 'c1', company_name: 'Acme', project_id: 'p2', project_name: 'Project B' },
 *   { company_id: 'c2', company_name: 'Beta', project_id: 'p3', project_name: 'Project C' },
 * ]
 * groupProjectsByCompany(flat)
 * // [
 * //   { id: 'c1', name: 'Acme', projects: [{ id: 'p1', name: 'Project A' }, { id: 'p2', name: 'Project B' }] },
 * //   { id: 'c2', name: 'Beta', projects: [{ id: 'p3', name: 'Project C' }] },
 * // ]
 */
export const groupProjectsByCompany = (
  flatProjects: ResponseProjectDropdown[]
): CompanyDropdown[] => {
  const byCompanyId = new Map<
    string,
    { name: string; projects: { id: string; name: string }[] }
  >()

  for (const row of flatProjects) {
    const existing = byCompanyId.get(row.company_id)
    const project = { id: row.project_id, name: row.project_name }
    if (existing) {
      existing.projects.push(project)
    } else {
      byCompanyId.set(row.company_id, {
        name: row.company_name,
        projects: [project],
      })
    }
  }

  return Array.from(byCompanyId.entries()).map(([id, { name, projects }]) => ({
    id,
    name,
    projects,
  }))
}
